/* Flat Rent Budapest links keep rel="noopener" WITHOUT "noreferrer" for the
   same reason the Lynqu section does: the referrer must reach
   flatrentbudapest.com so the backlink registers as attributed referral
   traffic and stays a dofollow link. noopener already neutralises the
   window.opener concern. */
/* eslint-disable react/jsx-no-target-blank */
import "../../styles/flatrent.css";
import FrbIcon from "../../assets/frb/frb-icon.svg";
import FrbDesktop from "../../assets/frb/frb-home-desktop.webp";
import FrbPhone from "../../assets/frb/frb-flats-iphone.webp";

const FRB_URL = "https://flatrentbudapest.com";

// What the product is, in the visitor's terms: who lists, what is listed, for whom.
/* Three chips fit one row at every breakpoint; a fourth wrapped. "No fees"
 * was also the one claim the paragraph above already makes. */
const FEATURES = ["Owner-direct", "Rent · Buy · Short stays", "English & Hungarian"];

// Focus ring tuned to the paper background (the Lynqu one is tuned to black).
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f6b6b] focus-visible:ring-offset-2 focus-visible:ring-offset-[#faf7f2]";

function ArrowUpRight({ className }) {
  return (
    <svg
      className={className}
      viewBox='0 0 24 24'
      width='18'
      height='18'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'>
      <path d='M7 17 17 7' />
      <path d='M7 7h10v10' />
    </svg>
  );
}

export default function FlatRentProduct(props) {
  const inView = props.inView;

  /* Staggered reveal: one transition, only the delay changes.
   * `transition-[opacity,transform]`, not `transition-all` — the latter
   * animates every animatable property, layout ones included.
   * The observer feeding `inView` is triggerOnce, so an entrance plays once
   * instead of replaying every time the section scrolls back into view. */
  const reveal = (delay) => ({
    className: `transition-[opacity,transform] duration-700 ease-out ${
      inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
    }`,
    style: { transitionDelay: `${delay}ms` },
  });

  return (
    <section
      id='flatrent'
      ref={props.sectionRef}
      className='frb-section relative w-full min-h-screen overflow-hidden bg-[#faf7f2] text-[#1b1b1f] flex items-center'>
      {/* Atmosphere: the product's own teal-to-ochre wash, no CSS filter. */}
      <div className='pointer-events-none absolute inset-0 frb-wash' />
      <div className='pointer-events-none absolute inset-0 frb-grain' />

      <div ref={props.revealRef} className='relative z-10 w-full max-w-[1500px] mx-auto px-[28px] nav-sm:px-[40px] c-lg:px-[80px] c-xl:px-[112px] py-[96px] nav-sm:py-[80px] flex flex-col nav-sm:flex-row items-center gap-[56px] c-lg:gap-[80px]'>
        {/* ---- Copy column ---- */}
        <div className='w-full nav-sm:w-[46%] order-2 nav-sm:order-1'>
          {/* Headline */}
          <h2
            {...reveal(80)}
            className={`${reveal(80).className} font-bold leading-[1.05] text-36 c-md:text-42 c-lg:text-50 c-xl:text-56`}>
            Our own city.
            <br className='hidden c-md:block' />{" "}
            <span className='text-[#0f6b6b]'>Listed by the people who own it.</span>
          </h2>

          {/* Brand name — dofollow brand-anchor link (the product's wordmark is
              set in type on its own site, so it is set in type here too). */}
          <a
            {...reveal(160)}
            href={FRB_URL}
            target='_blank'
            rel='noopener'
            aria-label='Flat Rent Budapest — visit flatrentbudapest.com'
            className={`${reveal(160).className} ${FOCUS_RING} group mt-[30px] inline-flex items-center gap-[12px] rounded-md`}>
            <img src={FrbIcon} alt='' width={32} height={32} className='w-[40px] h-[40px] c-lg:w-[48px] c-lg:h-[48px] rounded-[11px] shadow-[0_10px_24px_-10px_rgba(11,79,79,0.5)]' />
            <span className='frb-wordmark text-26 c-md:text-30 c-lg:text-36 leading-none transition-transform duration-300 group-hover:translate-x-[2px]'>
              Flat Rent <span className='text-[#0f6b6b]'>Budapest</span>
            </span>
          </a>

          <p
            {...reveal(220)}
            className={`${reveal(220).className} mt-[20px] max-w-[520px] font-semibold text-16 c-md:text-20 c-xl:text-22 leading-[1.4]`}>
            A free marketplace for renting and buying homes in Budapest and the towns around it.{" "}
            <span className='text-[#0f6b6b]'>No agency, no fees, nothing in between.</span>
          </p>

          {/* Feature chips */}
          <div
            {...reveal(280)}
            className={`${reveal(280).className} mt-[28px] flex flex-wrap gap-[10px]`}>
            {FEATURES.map((f) => (
              <span
                key={f}
                className='rounded-full border border-[#e7e0d4] bg-white/70 px-[16px] py-[8px] text-13 c-lg:text-14 text-[#1b1b1f]'>
                {f}
              </span>
            ))}
          </div>

          {/* CTA — primary dofollow link */}
          <div
            {...reveal(380)}
            className={`${reveal(380).className} mt-[36px] flex flex-wrap items-center gap-[18px]`}>
            <a
              href={FRB_URL}
              target='_blank'
              rel='noopener'
              className={`frb-cta ${FOCUS_RING} group inline-flex items-center gap-[9px] rounded-full px-[28px] py-[15px] min-h-[48px] text-15 c-lg:text-16 font-semibold text-white`}>
              Explore Flat Rent Budapest
              <ArrowUpRight className='transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]' />
            </a>
            <a
              href={FRB_URL}
              target='_blank'
              rel='noopener'
              className={`${FOCUS_RING} inline-flex items-center min-h-[44px] rounded-md px-[6px] text-14 c-lg:text-15 text-[#6b6b70] hover:text-[#0f6b6b] transition-colors duration-200 underline-offset-4 hover:underline`}>
              flatrentbudapest.com
            </a>
          </div>
        </div>

        {/* ---- Device column: the home page in a browser card, the listings
             feed on a phone overlapping its lower-left corner. ---- */}
        <div className='w-full nav-sm:w-[54%] order-1 nav-sm:order-2 relative flex items-center justify-center'>
          <div
            className={`relative w-full max-w-[720px] pb-[90px] c-md:pb-[110px] pl-[44px] c-md:pl-[110px] c-lg:pl-[150px] transition-[opacity,transform] duration-1000 ease-out ${
              inView ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-95"
            }`}>
            <div className='frb-browser rounded-[18px] overflow-hidden bg-white shadow-[0_30px_70px_-24px_rgba(11,79,79,0.45),0_2px_6px_rgba(27,27,31,0.06)]'>
              <div className='flex items-center gap-[6px] h-[30px] px-[14px] border-b border-[#e7e0d4] bg-[#f1ebdf]'>
                <span className='w-[9px] h-[9px] rounded-full bg-[#d9d2c4]' />
                <span className='w-[9px] h-[9px] rounded-full bg-[#d9d2c4]' />
                <span className='w-[9px] h-[9px] rounded-full bg-[#d9d2c4]' />
                <span className='ml-[10px] flex-1 h-[16px] rounded-full bg-white/80 text-11 leading-[16px] text-[#5c5c61] px-[10px] truncate'>
                  flatrentbudapest.com
                </span>
              </div>
              <img
                src={FrbDesktop}
                alt='Flat Rent Budapest home page: find a flat in Budapest from the people who own it'
                width={1600}
                height={1000}
                className='block w-full h-auto'
                loading='lazy'
              />
            </div>
            <div className='frb-float absolute left-0 bottom-0 w-[130px] c-md:w-[180px] c-lg:w-[210px]'>
              <div className='frb-phone rounded-[30px] c-md:rounded-[36px] p-[7px] c-md:p-[9px] bg-[#1b1b1f] shadow-[0_34px_60px_-20px_rgba(27,27,31,0.55)]'>
                <img
                  src={FrbPhone}
                  alt='Flat Rent Budapest listings on a phone'
                  width={780}
                  height={1688}
                  className='block w-full h-auto rounded-[24px] c-md:rounded-[28px]'
                  loading='lazy'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
